<template>
  <div class="bg-white p-3 mb-3">
    <div class="mb-3">
      <span class="border rounded-circle shadow-sm p-1 mr-2">
        <i class="fas fa-flag text-orange" />
      </span>
      <span class="font-weight-bold text-gray-dark">
        {{ isReadOnly ? 'Determinación' : 'Determinar' }}
      </span>
    </div>

    <validation-observer ref="determinateForm">
      <div class="row">
        <div class="col-12 col-md-4">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            :custom-messages="{
              required: 'El campo nombre es requerido.',
            }"
          >
            <label class="data-label mb-0">Nombre:</label>
            <custom-input
              v-model="model.name"
              :disabled="isReadOnly"
              :error="errors[0]"
              :custom-error-msg="errors[0]"
            />
          </validation-provider>
        </div>
        <div class="col-12 col-md-4">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            :custom-messages="{
              required: 'El campo apellido paterno es requerido.',
            }"
          >
            <label class="data-label mb-0">Apellido Paterno:</label>

            <custom-input
              v-model="model.paternalSurname"
              :disabled="isReadOnly"
              :error="errors[0]"
              :custom-error-msg="errors[0]"
            />
          </validation-provider>
        </div>
        <div class="col-12 col-md-4">
          <label class="data-label mb-0">Apellido Materno:</label>

          <custom-input
            v-model="model.maternalSurname"
            :disabled="isReadOnly"
          />
        </div>
        <div class="col-12 col-md-4 px-0 mt-3">
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            :custom-messages="{
              required: 'El campo apellido materno es requerido.',
            }"
          >
            <custom-radio
              v-model="model.hasHipaa"
              class="custom-radio__extended"
              custom-error-msg="Favor indicar si documento será requerido."
              :error="errors[0]"
              :is-disabled="isReadOnly"
              is-inline
              :options="requiredOptions"
              label="¿El salón cumple con HIPAA?"
            />
          </validation-provider>
        </div>
      </div>
      <div v-if="model.hasHipaa">
        <section-title
          content="Documentos relacionados a la Determinación"
          icon="fas fa-cloud-upload"
          reverse
          class="my-3"
        />
        <documents
          v-model="model.documents"
          :document-types="documentTypes"
          :other-type-id="otherTypeId"
          :is-read-only="isReadOnly"
          add-variant="bg-gray p-3 rounded"
        />
      </div>
      <div v-else-if="model.hasHipaa == false" class="my-3">
        <section-title
          v-if="isReadOnly"
          content="Mejoras añadidas"
          icon="fas fa-check-circle"
          reverse
          class="my-3"
        />
        <custom-todo
          v-model="model.improvements"
          label="Mejoras debe recibir el salón:"
          :separator="isReadOnly ? '' : 'Mejoras añadidas'"
          :is-read-only="isReadOnly"
        />
      </div>

      <div class="d-flex justify-content-end align-items-center mt-3">
        <action-button
          i="fas fa-times-circle"
          text="Cancelar"
          variant="outline-danger mr-2"
          @click="cancel"
        />
        <action-button
          v-if="!isReadOnly"
          i="fas fa-save"
          text="Guardar"
          variant="success"
          @click="save"
        />
      </div>
    </validation-observer>
  </div>
</template>

<script>
import alert from '@/mixin/alert.mixin';

import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import CustomRadio from 'custom-radio';
import Documents from './Documents.vue';
import SectionTitle from 'SectionTitle';
import CustomTodo from '@/components/common/CustomTodo.vue';
import DocumentTypes, { Categories } from '@/utils/constants/documentTypes';

import * as api from '@/views/pei/location-alternatives/_api';

export default {
  name: 'Determination',
  mixins: [alert],
  components: {
    CustomInput,
    ActionButton,
    CustomRadio,
    SectionTitle,
    Documents,
    CustomTodo,
  },
  props: {
    alternativeId: {
      type: Number,
      default: () => null,
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      documentTypes: [],
      model: {
        name: '',
        paternalSurname: '',
        maternalSurname: '',
        hasHipaa: null,
        documents: [],
        improvements: [],
      },
    };
  },
  async mounted() {
    if (this.isReadOnly) {
      await this.getDetermination();
    } else {
      await this.getDocumentTypes();
    }
  },
  computed: {
    otherTypeId() {
      return DocumentTypes.DeterminateTapAlternativeLocationDocument;
    },
    requiredOptions() {
      return [
        {
          id: 1,
          value: true,
          label: 'Si',
        },
        {
          id: 2,
          value: false,
          label: 'No',
        },
      ];
    },
  },
  methods: {
    async getDetermination() {
      const { data } = await api.getDetermination(this.alternativeId);
      this.model = data;
    },
    async getDocumentTypes() {
      this.documentTypes = await api.getDocumentTypesByCategory(
        Categories.DeterminateTapAlternativeLocationDocument
      );
    },
    async save() {
      const valid = await this.$refs.determinateForm.validate();
      if (!valid) {
        this.ShowToast(
          'Error',
          'Favor de completar información para continuar',
          'error'
        );
        return;
      }
      if (this.model.hasHipaa && !this.model.documents?.length) {
        this.ShowToast(
          'Error',
          'Favor de añadir al menos un documento para continuar',
          'error'
        );
        return;
      }

      if (!this.model.hasHipaa && !this.model.improvements?.length) {
        this.ShowToast(
          'Error',
          'Favor de añadir al menos una mejora para continuar',
          'error'
        );
        return;
      }

      this.$emit('save', this.model);
    },
    cancel() {
      this.$emit('cancel');
      this.$refs.determinateForm.reset();
    },
  },
};
</script>

<style scoped>
::v-deep .input-display {
  margin-left: 0 !important;
}
</style>
